import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import { ClaimsAdminTopNav } from '@src/components/Claims/ClaimsAdminTopNav';
import { PaymentRequestInProcessTag } from '@src/components/PaymentRequest/PaymentRequestInProcessTag';
import { PaymentRequestStatusTag } from '@src/components/PaymentRequestStatusTag';
import { SET_PAYMENT_REQUEST_IN_PROCESS } from '@src/graphql/queries/paymentRequest';
import { useAuth } from '@src/hooks/useAuth';
import { ClaimsTab, usePaymentRequestClaims } from '@src/hooks/usePaymentRequestClaims';
import { getFullName } from '@src/utilities/nameHelper';
import { compact } from 'lodash';
import { DataTable, Messages, TrackableButton, useLocale, useSessionState, } from 'pe-core-frontend';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
export const PaymentRequestClaimsList = ({ tab, showFilter = false, }) => {
    const { isSuperuser, isClaimsProcessor } = useAuth();
    const navigate = useNavigate();
    const { t, formatToLocalDatetime, formatCurrency } = useLocale();
    const [filter, setFilter] = useSessionState(`paymentRequestFilter_${tab}`, {
        searchText: undefined,
        clientId: undefined,
        startDate: undefined,
        endDate: undefined,
    });
    const isProcessedTab = tab === ClaimsTab.PROCESSED;
    const isInfoRequestedTab = tab === ClaimsTab.INFO_REQUESTED;
    const [isSaving, setIsSaving] = useState(false);
    const [messages, setMessages] = useState([]);
    const { paymentRequests, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, setOrderBy, orderByDir, setOrderByDir, } = usePaymentRequestClaims({
        filter,
        tab,
        initialPageSize: 10,
    });
    const [setInProcess] = useMutation(SET_PAYMENT_REQUEST_IN_PROCESS);
    const handleSetInProcess = async (id) => {
        setIsSaving(true);
        try {
            const { data } = await setInProcess({ variables: { id, inProcess: true } });
            if (data?.setPaymentRequestInProcess?.messages) {
                setMessages(data.setPaymentRequestInProcess.messages);
            }
            else {
                navigate(`/admin/claims/payments/${id}`);
            }
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1);
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const columns = compact([
        {
            field: 'requestNum',
            headerName: t('Request #'),
            width: 110,
            flex: 1.1,
        },
        {
            field: 'employeeName',
            headerName: t('Employee Name'),
            width: 200,
            flex: 2,
            sortable: false,
            renderCell: ({ row }) => _jsx("span", { children: getFullName(row.program.employee) }),
        },
        {
            field: 'employeeId',
            headerName: t('Employee ID'),
            width: 140,
            flex: 1.4,
            sortable: false,
            renderCell: ({ row }) => row.program.employee.uniqueId,
        },
        {
            field: 'clientName',
            headerName: t('Employer'),
            width: 150,
            flex: 1.5,
            sortable: false,
            renderCell: ({ row }) => row.client.name,
        },
        {
            field: 'submittedAt',
            headerName: t('Submitted'),
            width: 140,
            flex: 1.4,
            sortable: true,
            renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'medium' }),
        },
        isProcessedTab && {
            field: 'status',
            headerName: t('Status'),
            width: 100,
            flex: 1.0,
            sortable: false,
            renderCell: ({ row }) => _jsx(PaymentRequestStatusTag, { paymentRequest: row }),
        },
        {
            field: 'amountRequested',
            headerName: t('Requested'),
            width: 150,
            flex: 1.5,
            headerAlign: 'right',
            align: 'right',
            sortable: false,
            renderCell: ({ value, row }) => formatCurrency(value, row.client.clientConfig.currency, true),
        },
        (isProcessedTab || isInfoRequestedTab) && {
            field: 'processedBy',
            headerName: t('Processed By'),
            width: 160,
            flex: 1.6,
            sortable: false,
            renderCell: ({ row }) => (row.processedBy ? getFullName(row.processedBy) : ''),
        },
    ]);
    if (isProcessedTab) {
        columns.push({
            field: 'amountApproved',
            headerName: t('Approved'),
            width: 150,
            flex: 1.5,
            headerAlign: 'right',
            align: 'right',
            sortable: false,
            renderCell: ({ value, row }) => formatCurrency(value, row.client.clientConfig.currency, true),
        });
        columns.push({
            field: 'amountToPay',
            headerName: t('Paid'),
            width: 150,
            flex: 1.5,
            headerAlign: 'right',
            align: 'right',
            sortable: false,
            renderCell: ({ value, row }) => formatCurrency(value, row.client.clientConfig.currency, true),
        });
        columns.push({
            field: 'processedDate',
            headerName: t('Processed'),
            width: 160,
            flex: 1.6,
            sortable: true,
            renderCell: ({ value, row }) => (_jsxs("div", { className: 'flex flex-col', children: [_jsx("div", { children: formatToLocalDatetime(row.processedDate, { dateStyle: 'medium' }) }), _jsx("div", { children: getFullName(value ?? {}) })] })),
        });
    }
    columns.push({
        field: 'actions',
        headerName: t('Actions'),
        width: 180,
        flex: 1.8,
        sortable: false,
        renderCell: ({ row }) => (_jsxs("div", { className: 'p-1 flex gap-2 items-center', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'View', tag: 'claims_list', variant: 'outlined', onClick: () => navigate(`/admin/claims/payments/${row.id}`), children: t('View') }), [ClaimsTab.PENDING, ClaimsTab.HOLD].includes(tab) &&
                    isClaimsProcessor &&
                    (row.inProcess && row.processedBy ? (_jsx(PaymentRequestInProcessTag, { paymentRequest: row })) : (_jsx(TrackableButton, { btnType: 'mui', enText: 'Process', tag: 'claims_list', disabled: isSaving, variant: 'outlined', onClick: () => handleSetInProcess(row.id), children: t('Process') })))] })),
    });
    return (_jsxs("div", { children: [tab && (_jsx(ClaimsAdminTopNav, { tab: tab, showFilter: showFilter, filter: filter, handleFilterChange: handleFilterChange, isSuperuser: isSuperuser })), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { hideClose: true, display: true, type: 'danger', children: t('Failed to load claims') })), !error && total === 0 && (_jsx(Alert, { hideClose: true, display: true, type: 'info', children: t('No claims found') })), _jsx(Messages, { messages: messages }), !error && total > 0 && (_jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: paymentRequests, pageSizeOptions: [10, 25], paginationModel: { page: page - 1, pageSize }, paginationMode: 'server', onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, loading: loading, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [
                                    {
                                        field: orderBy ?? 'createdAt',
                                        sort: orderByDir,
                                    },
                                ], columns: columns }))] }) }) })] }));
};
